import React, { createContext, useCallback, useContext, useMemo, useRef, useSyncExternalStore } from 'react';

/**
 * Context that holds a ref to the currently active EnhancedRichTextEditor instance.
 * Components containing an editor register/unregister via `registerEditor` /
 * `unregisterEditor`; the chat (Inky) reads the editor bridge via `getEditorRef().current`
 * (see CONTRACTS §6: getMeta, getBlocks, getSelection, applyEditPlan, review API, on()).
 *
 * `subscribe` / `useActiveEditorPresence` let consumers react when an editor mounts or
 * unmounts (e.g. only offer document tools while a document is open).
 */
const ActiveEditorContext = createContext(null);

/**
 * Provider that manages the active editor reference.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} Provider wrapper.
 */
const ActiveEditorProvider = ({ children }) => {
  const editorRefStore = useRef(null);
  const versionRef = useRef(0);
  const listenersRef = useRef(new Set());

  /** Notify presence subscribers. */
  const notify = useCallback(() => {
    versionRef.current += 1;
    listenersRef.current.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        console.error('ActiveEditor listener failed:', error);
      }
    });
  }, []);

  /**
   * Register an editor ref as the currently active editor.
   * @param {React.RefObject} ref - The editor component ref.
   */
  const registerEditor = useCallback((ref) => {
    if (editorRefStore.current === ref) return;
    editorRefStore.current = ref;
    notify();
  }, [notify]);

  /**
   * Unregister the active editor (e.g. when the editor component unmounts).
   * @param {React.RefObject} ref - The ref to unregister (only clears if it matches).
   */
  const unregisterEditor = useCallback((ref) => {
    if (editorRefStore.current === ref) {
      editorRefStore.current = null;
      notify();
    }
  }, [notify]);

  /**
   * Get the currently active editor ref.
   * @returns {React.RefObject | null} The active editor ref, or null.
   */
  const getEditorRef = useCallback(() => editorRefStore.current, []);

  /**
   * Get the active editor bridge (the imperative handle), if mounted.
   * @returns {object|null} Editor bridge.
   */
  const getActiveEditor = useCallback(() => {
    const bridge = editorRefStore.current?.current || null;
    if (!bridge || (typeof bridge.isDestroyed === 'function' && bridge.isDestroyed())) return null;
    return bridge;
  }, []);

  /**
   * Subscribe to register/unregister changes.
   * @param {() => void} listener - Called after the active editor changes.
   * @returns {() => void} Unsubscribe.
   */
  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  /**
   * Snapshot version for useSyncExternalStore.
   * @returns {number} Version counter.
   */
  const getVersion = useCallback(() => versionRef.current, []);

  const value = useMemo(
    () => ({ registerEditor, unregisterEditor, getEditorRef, getActiveEditor, subscribe, getVersion }),
    [registerEditor, unregisterEditor, getEditorRef, getActiveEditor, subscribe, getVersion]
  );

  return (
    <ActiveEditorContext.Provider value={value}>
      {children}
    </ActiveEditorContext.Provider>
  );
};

/**
 * Hook to access the active editor context.
 * @returns {{ registerEditor: Function, unregisterEditor: Function, getEditorRef: Function, getActiveEditor: Function, subscribe: Function, getVersion: Function }}
 */
const useActiveEditor = () => {
  const context = useContext(ActiveEditorContext);
  if (!context) {
    throw new Error('useActiveEditor must be used within an ActiveEditorProvider');
  }
  return context;
};

/**
 * Re-render when an editor registers/unregisters and return its metadata.
 * @returns {{documentId: string|null, templateId: string|null, kind: 'document'|'template', title: string|null, readOnly: boolean}|null} Active editor meta, or null.
 */
const useActiveEditorPresence = () => {
  const { subscribe, getVersion, getActiveEditor } = useActiveEditor();
  useSyncExternalStore(subscribe, getVersion);
  const bridge = getActiveEditor();
  return bridge && typeof bridge.getMeta === 'function' ? bridge.getMeta() : null;
};

export { ActiveEditorProvider, useActiveEditor, useActiveEditorPresence };
